import type { CardCost, Die, DieColor } from '../types';

function dieValues(dice: Die[]): number[] {
  return dice.map((d) => d.value).filter((v): v is number => v != null);
}

function sum(dice: Die[]): number {
  return dieValues(dice).reduce((a, b) => a + b, 0);
}

function colorCount(dice: Die[], color: DieColor): number {
  return dice.filter((d) => d.color === color).length;
}

function hasNOfAKind(values: number[], n: number): boolean {
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  for (const c of counts.values()) if (c >= n) return true;
  return false;
}

function hasNDistinctColors(dice: Die[], n: number): boolean {
  return new Set(dice.map((d) => d.color)).size >= n;
}

function hasRunOf(values: number[], n: number): boolean {
  const sorted = Array.from(new Set(values)).sort((a, b) => a - b);
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i]! === sorted[i - 1]! + 1) {
      run++;
      if (run >= n) return true;
    } else {
      run = 1;
    }
  }
  return false;
}

export function canAfford(cost: CardCost, dice: Die[]): boolean {
  if (dice.some((d) => d.value == null)) return false;
  switch (cost.kind) {
    case 'sumMin':
      return sum(dice) >= cost.min;
    case 'sumExact':
      return sum(dice) === cost.total;
    case 'sumMinWithColor':
      return (
        sum(dice) >= cost.min &&
        colorCount(dice, cost.color) >= cost.count
      );
    case 'set': {
      const values = dieValues(dice);
      switch (cost.pattern) {
        case 'pair':
          return hasNOfAKind(values, 2);
        case 'threeOfAKind':
          return hasNOfAKind(values, 3);
        case 'threeColors':
          return hasNDistinctColors(dice, 3);
        case 'run3':
          return hasRunOf(values, 3);
      }
    }
  }
}

export function describeCost(cost: CardCost): string {
  switch (cost.kind) {
    case 'sumMin':
      return `Sum ≥ ${cost.min}`;
    case 'sumExact':
      return `Sum = ${cost.total}`;
    case 'sumMinWithColor': {
      const c = cost.color[0]!.toUpperCase() + cost.color.slice(1);
      const n = cost.count === 1 ? '' : `${cost.count} `;
      return `Sum ≥ ${cost.min} + ${n}${c}`;
    }
    case 'set':
      switch (cost.pattern) {
        case 'pair':
          return 'Pair';
        case 'threeOfAKind':
          return 'Three of a kind';
        case 'threeColors':
          return 'Three colors';
        case 'run3':
          return 'Run of 3';
      }
  }
}
